import { useReducedMotion } from "@react-spring/web";
import { initializeTcDataAnalyticsLogging } from "@rome2rio/tcf-api";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AnalyticsEventCategory } from "./analytics/analyticsEventTypes";
import messages from "./App.messages";
import { useInitializeAppleSignin } from "./auth/sdks/AppleAuthSdk";
import { useInitializeGoogleSignin } from "./auth/sdks/GoogleAuthSdkHooks";
import { BottomNavbar } from "./components/BottomNavbar/BottomNavbar";
import { DefaultErrorBoundary } from "./components/DefaultErrorBoundary/DefaultErrorBoundary";
import { Layout } from "./components/Layout/Layout";
import { RightRail } from "./components/RightRail/RightRail";
import { registerAdaraConsent } from "./data-partners/adara/registerAdaraConsent";
import { registerMarketingDataPartners } from "./data-partners/registerDataPartner";
import { HotelsProvider } from "./domain/HotelsScreen/HotelsContext";
import { ReturnFlowStateProvider } from "./domain/SegmentScreen/ReturnFlowStateProvider";
import { TripPlannerProvider } from "./domain/TripPlanner/TripPlannerProvider";
import { UNSAFE_useConfigureFeatureEscapeHatch } from "./feature/UNSAFE_useConfigureFeatureEscapeHatch";
import { useFeature } from "./feature/useFeature";
import { HoveredIndexProvider } from "./HoveredIndexProvider";
import MapPane from "./MapPane";
import { PaneContent } from "./PaneContent";
import { ScrollContext } from "./ScrollContext";
import { useCachedResponse } from "./utils/hooks/useCachedResponse";
import { useGetPermaLink } from "./utils/hooks/useGetPermaLink";
import { useHeadMeta } from "./utils/hooks/useHeadMeta";
import { initializeHotjarOnConsent } from "./utils/hooks/useHotjar";
import { useIsTripScreen } from "./utils/hooks/useIsTripScreen";
import { useLanguageAttribute } from "./utils/hooks/useLanguageAttribute";
import { useLayout } from "./utils/hooks/useLayout";
import { useMarker } from "./utils/hooks/useMarker";
import { useIsHotelsUrlDeeplink } from "./utils/hooks/useNavigateToHotelsPage";
import useSearch from "./utils/hooks/useSearch";
import { useSwitchToHotelsNear } from "./utils/hooks/useSwitchToHotelsNear";
import { useTypedLocation } from "./utils/hooks/useTypedLocation";
import logBreadcrumbs from "./utils/logBreadcrumbs";
import logContext from "./utils/logContext";
import { useDetectAdBlocker } from "./utils/useDetectAdBlocker";
import { registerGoogleConsent } from "./data-partners/google/registerGoogleConsent";
import { GlobalHeader } from "./components/GlobalHeader/GlobalHeader";
import { Footer } from "./domain/Footer/Footer";
import type { SearchResponse } from "./api/SearchResponse";
import { useScreenMinWidth } from "./utils/hooks/useScreenMinWidth";

export type MobileTabs = "search" | "preferences" | "hotels" | "trips";

export function App() {
  UNSAFE_useConfigureFeatureEscapeHatch();
  const intl = useIntl();
  const location = useTypedLocation();
  const { returnsFlowLocation } = location.state ?? {};
  const { origin, destination, searchResponse } = useSearch();
  const permalink = useGetPermaLink();
  const isTripScreen = useIsTripScreen();
  const reactQueryDevTools = useFeature("ReactQueryDevTools");
  const isHotelsScreen = useIsHotelsUrlDeeplink();
  useHeadMeta({
    title: getPageTitle(intl, origin, destination, searchResponse),
    description: getPageDescription(
      intl,
      destination,
      searchResponse,
      isHotelsScreen,
    ),
  });
  useLanguageAttribute();
  useSwitchToHotelsNear();
  useInitializeGoogleSignin();
  useInitializeAppleSignin();
  useReducedMotion();
  useMarker();

  useEffect(() => {
    initializeTcDataAnalyticsLogging((category, action, label, value) =>
      sendAnalyticsNonInteractionEvent({
        category: category as unknown as AnalyticsEventCategory,
        action: action,
        label: label,
        value: value,
      }),
    );
    initializeHotjarOnConsent();
    registerMarketingDataPartners();
  }, []);

  useEffect(() => {
    registerAdaraConsent(
      searchResponse?.analytics?.interest_data?.Adara?.enabled,
    );
    registerGoogleConsent();
  }, [searchResponse?.analytics?.interest_data?.Adara?.enabled]);

  const adBlockerDetected = useDetectAdBlocker();

  useEffect(() => {
    if (adBlockerDetected) {
      sendAnalyticsNonInteractionEvent({
        category: "App",
        action: "AdBlockerDetected",
      });
    }
  }, [adBlockerDetected]);

  const layout = useLayout((layout, lastLayout) => {
    logBreadcrumbs("Layout", `Layout change: ${lastLayout} -> ${layout}`);
  });

  const { isMdScreen } = useScreenMinWidth();

  // If the user pastes a saved url, we need to default to the correct tab on mobile.
  function chooseDefaultTabHighlight() {
    if (isTripScreen) {
      return "trips";
    } else if (isHotelsScreen) {
      return "hotels";
    }
    return "search";
  }

  const geocodeOrigin = origin?.canonicalName;
  const geocodeDestination = destination?.canonicalName;
  const responseOrigin = searchResponse?.request?.oName;
  const responseDestination = searchResponse?.request?.dName;
  useEffect(() => {
    logContext({
      layout: layout,
      responseOrigin: responseOrigin,
      responseDestination: responseDestination,
      geocodeOrigin: geocodeOrigin,
      geocodeDestination: geocodeDestination,
      permalink: permalink,
    });
  }, [
    layout,
    responseOrigin,
    responseDestination,
    geocodeOrigin,
    geocodeDestination,
    permalink,
  ]);

  // On mobile default to using the <html /> element for scrolling
  const scrollRef = useRef<HTMLElement>(document.documentElement);
  const getScrollTop = useCallback(() => scrollRef.current?.scrollTop ?? 0, []);
  const setScrollTop = useCallback((top: number) => {
    if (scrollRef.current) scrollRef.current.scrollTop = top;
  }, []);

  const highlightedTab =
    location.state?.highlightedTab ?? chooseDefaultTabHighlight();

  // Cache the search response for the map so that when a new request
  // is made the map doesn't get refreshed.
  const cachedResponse = useCachedResponse(searchResponse);

  const UseScheduleDetailScreen =
    location.state?.openScheduleIndex !== undefined && !isHotelsScreen;

  const isMapEnabled = layout !== "mobile" || isHotelsScreen;
  const wasMapEnabled = useLatch(isMapEnabled);

  // Once the map is loaded, keep it loaded even when it is not visible
  // so that we can avoid reloading the map which costs us money.
  const shouldRenderMap = !!((isMapEnabled || wasMapEnabled) && cachedResponse);

  const shouldShowRightRail = isMdScreen;

  return (
    <ScrollContext
      value={{
        getScrollTop,
        setScrollTop,
        scrollRef,
      }}
    >
      <HoveredIndexProvider>
        <TripPlannerProvider>
          <ReturnFlowStateProvider returnsFlowLocation={returnsFlowLocation}>
            <HotelsProvider>
              <div className="flex flex-col">
                <h1 className="sr-only">
                  {intl.formatMessage(messages.appName)}
                </h1>
                {layout !== "mobile" && <GlobalHeader />}
                <DefaultErrorBoundary defaultMessage>
                  <Layout
                    ref={(el) => {
                      if (el && layout === "desktop") {
                        scrollRef.current = el;
                      }
                    }}
                    leftPane={
                      <PaneContent
                        highlightedTab={highlightedTab}
                        layout={layout}
                        sectionTitle={intl.formatMessage(
                          messages.resultsSectionTitle,
                        )}
                        openScheduleIndex={location.state?.openScheduleIndex}
                        returnsFlowLocation={returnsFlowLocation}
                      />
                    }
                    background={
                      isMapEnabled ? (
                        <MapPane
                          shouldRenderMap={shouldRenderMap}
                          searchResponse={cachedResponse}
                          returnsFlowLocation={returnsFlowLocation}
                        />
                      ) : null
                    }
                    rightRail={shouldShowRightRail && <RightRail />}
                  />
                </DefaultErrorBoundary>
                <Footer />
                {layout === "mobile" && !UseScheduleDetailScreen && (
                  <BottomNavbar
                    active={highlightedTab}
                    setScrollTop={setScrollTop}
                  />
                )}
              </div>
            </HotelsProvider>
          </ReturnFlowStateProvider>
        </TripPlannerProvider>
      </HoveredIndexProvider>
      {reactQueryDevTools && <ReactQueryDevtools initialIsOpen={false} />}
    </ScrollContext>
  );
}

function getPageTitle(
  intl: ReturnType<typeof useIntl>,
  origin?: { shortName: string },
  destination?: { shortName: string },
  searchResponse?: SearchResponse,
) {
  if (searchResponse?.result.seoTitle) {
    return searchResponse.result.seoTitle;
  } else if (origin && destination) {
    return intl.formatMessage(messages.pageTitle, {
      origin: origin.shortName,
      destination: destination.shortName,
    });
  }
  return "Rome2Rio";
}

function getPageDescription(
  intl: ReturnType<typeof useIntl>,
  destination?: { shortName: string },
  searchResponse?: SearchResponse,
  isHotelsScreen?: boolean,
) {
  if (isHotelsScreen) {
    return intl.formatMessage(messages.hotelDescription, {
      destination: destination?.shortName,
    });
  } else if (searchResponse?.result.seoDescription) {
    return searchResponse.result.seoDescription;
  }
  return intl.formatMessage(messages.description);
}

/**
 * A hook that returns true if the value passed in was ever true.
 */
function useLatch(value: boolean): boolean {
  const ref = useRef(value);
  useEffect(() => {
    ref.current ||= value;
  }, [value]);
  return ref.current;
}
